// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { VirtualBudtenderQuery } from '@iheartjane/dm-sdk';
import {
  Box,
  Flex,
  SparkleIcon,
  Typography,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { EventNames } from '@iheartjane/tracking';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { useMenu } from '@jane/shared-ecomm/providers';
import { track } from '@jane/shared-ecomm/tracking';

const COLORS = ['purple', 'sunset', 'grape'] as const;

const PillContainer = styled(Flex)({
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
});

const Pill = styled(Box)({
  cursor: 'pointer',
});

interface MenuVirtualBudtenderExplorePillsProps {
  virtualBudtenderQueries: VirtualBudtenderQuery[];
}

interface PillContentProps {
  colorIndex: number;
  minWidth: number;
  onClick: (displayText: string, query: string) => void;
  query: VirtualBudtenderQuery;
}

const PillContent = ({
  query,
  colorIndex,
  minWidth,
  onClick,
}: PillContentProps) => (
  <Pill
    key={query.query}
    borderRadius="sm"
    background={COLORS[colorIndex % COLORS.length]}
    p={8}
    minWidth={minWidth}
    height={128}
    onClick={() => onClick(query.displayText, query.query)}
  >
    <Flex
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="100%"
    >
      <Typography
        as="p"
        width="100%"
        variant="header-bold"
        color="grays-white"
        textAlign="center"
        maxLines={2}
        overflow="hidden"
        overflowWrap="anywhere"
      >
        {query.displayText}
      </Typography>
    </Flex>
  </Pill>
);

interface PillRowProps {
  minWidth: number;
  onClick: (displayText: string, query: string) => void;
  queries: VirtualBudtenderQuery[];
  startIndex: number;
}

const PillRow = ({ queries, startIndex, minWidth, onClick }: PillRowProps) => (
  <PillContainer gap={24} alignItems="center" overflowX="scroll">
    {queries.map((query, index) => (
      <PillContent
        key={query.query}
        query={query}
        colorIndex={startIndex + index}
        minWidth={minWidth}
        onClick={onClick}
      />
    ))}
  </PillContainer>
);

export const MenuVirtualBudtenderExplorePills = ({
  virtualBudtenderQueries,
}: MenuVirtualBudtenderExplorePillsProps) => {
  const navigate = useNavigate();
  const isMobile = useMobileMediaQuery({});
  const { paths, store } = useMenu();

  const queriesKey = useMemo(
    () => virtualBudtenderQueries.map((q) => q.query).join(','),
    [virtualBudtenderQueries]
  );

  // Track when queries are viewed
  useEffect(() => {
    if (virtualBudtenderQueries.length > 0) {
      track({
        event: EventNames.VirtualBudtenderQueriesViewed,
        storeId: store.id,
        storeName: store.name,
        numberOfQueries: virtualBudtenderQueries.length,
        queries: virtualBudtenderQueries.map((q) => q.displayText),
      });
    }
  }, [queriesKey, store.id, store.name]);

  const onClick = useCallback(
    (displayText: string, query: string) => {
      // Track when a specific query is clicked
      track({
        event: EventNames.VirtualBudtenderSearchSubmitted,
        storeId: store.id,
        storeName: store.name,
        query,
        queryDisplayText: displayText,
      });
      navigate(paths.vb({ displayText, query }));
    },
    [navigate, paths, store.id, store.name]
  );

  if (virtualBudtenderQueries.length === 0) {
    return null;
  }

  const renderPills = () => {
    if (isMobile) {
      const evenIndexQueries = virtualBudtenderQueries.filter(
        (_, index) => index % 2 === 0
      );
      const oddIndexQueries = virtualBudtenderQueries.filter(
        (_, index) => index % 2 === 1
      );

      return (
        <Flex flexDirection="column" gap={24}>
          <PillRow
            queries={evenIndexQueries}
            startIndex={0}
            minWidth={200}
            onClick={onClick}
          />
          <PillRow
            queries={oddIndexQueries}
            startIndex={1}
            minWidth={200}
            onClick={onClick}
          />
        </Flex>
      );
    }

    return (
      <PillRow
        queries={virtualBudtenderQueries}
        startIndex={0}
        minWidth={243}
        onClick={onClick}
      />
    );
  };

  return (
    <Flex flexDirection="column" justifyContent="space-between" width="100%">
      <Flex mb={24} gap={8}>
        <SparkleIcon color="primary" size="md" mt={4} />
        <Flex flexDirection="column">
          <Typography as="h2" branded variant="header-bold">
            Explore
          </Typography>
          <Typography color="grays-mid" textAlign="left">
            AI-powered suggestions
          </Typography>
        </Flex>
      </Flex>
      {renderPills()}
    </Flex>
  );
};
